import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Brain, Save, X } from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { aiModelsApi } from '../../services/adminApi';

interface AIProvider {
  id: string;
  name: string;
  display_name: string;
  is_active: boolean;
}

interface AIModel {
  id: string;
  provider_id: string;
  model_id: string;
  display_name: string;
  description: string;
  is_active: boolean;
  is_recommended: boolean;
  cost_tier: 'low' | 'medium' | 'high' | 'premium';
}

interface AIModelsProps {
  onNavigate: (page: string) => void;
}

export const AIModels: React.FC<AIModelsProps> = ({ onNavigate }) => {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAddProvider, setShowAddProvider] = useState(false);
  const [showAddModel, setShowAddModel] = useState(false);
  const [editingProvider, setEditingProvider] = useState<AIProvider | null>(null);
  const [editingModel, setEditingModel] = useState<AIModel | null>(null);

  const [providerForm, setProviderForm] = useState({
    name: '',
    display_name: '',
    is_active: true,
  });

  const [modelForm, setModelForm] = useState({
    provider_id: 'anthropic',
    model_id: '',
    display_name: '',
    description: '',
    is_active: true,
    is_recommended: false,
    cost_tier: 'medium' as 'low' | 'medium' | 'high' | 'premium',
  });

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [providersRes, modelsRes] = await Promise.all([
        aiModelsApi.listProviders(),
        aiModelsApi.listModels(),
      ]);

      if (!providersRes.success) {
        throw new Error(providersRes.message || 'Failed to load providers');
      }

      if (!modelsRes.success) {
        throw new Error(modelsRes.message || 'Failed to load models');
      }

      setProviders(providersRes.data);
      setModels(modelsRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async () => {
    if (!providerForm.name || !providerForm.display_name) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const result = await aiModelsApi.createProvider({
        name: providerForm.name.toLowerCase(),
        display_name: providerForm.display_name,
        is_active: providerForm.is_active,
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to create provider');
      }

      setProviders([...providers, result.data]);
      setShowAddProvider(false);
      setProviderForm({ name: '', display_name: '', is_active: true });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create provider');
    }
  };

  const handleEditProvider = (provider: AIProvider) => {
    setEditingProvider(provider);
    setProviderForm({
      name: provider.name,
      display_name: provider.display_name,
      is_active: provider.is_active,
    });
    setShowAddProvider(true);
  };

  const handleUpdateProvider = async () => {
    if (!editingProvider) return;

    try {
      const result = await aiModelsApi.updateProvider(editingProvider.id, {
        display_name: providerForm.display_name,
        is_active: providerForm.is_active,
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update provider');
      }

      setProviders(providers.map(p =>
        p.id === editingProvider.id ? result.data : p
      ));

      setEditingProvider(null);
      setShowAddProvider(false);
      setProviderForm({ name: '', display_name: '', is_active: true });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update provider');
    }
  };

  const handleDeleteProvider = async (id: string) => {
    if (!confirm('Are you sure you want to delete this provider? All associated models will also be removed.')) {
      return;
    }

    try {
      const result = await aiModelsApi.deleteProvider(id);

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete provider');
      }

      setProviders(providers.filter(p => p.id !== id));
      setModels(models.filter(m => m.provider_id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete provider');
    }
  };

  const handleAddModel = async () => {
    if (!modelForm.model_id || !modelForm.display_name) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const result = await aiModelsApi.createModel({
        provider_id: modelForm.provider_id,
        model_id: modelForm.model_id,
        display_name: modelForm.display_name,
        description: modelForm.description,
        is_active: modelForm.is_active,
        is_recommended: modelForm.is_recommended,
        cost_tier: modelForm.cost_tier,
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to create model');
      }

      setModels([...models, result.data]);
      setShowAddModel(false);
      setModelForm({
        provider_id: 'anthropic',
        model_id: '',
        display_name: '',
        description: '',
        is_active: true,
        is_recommended: false,
        cost_tier: 'medium',
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create model');
    }
  };

  const handleEditModel = (model: AIModel) => {
    setEditingModel(model);
    setModelForm({
      provider_id: model.provider_id,
      model_id: model.model_id,
      display_name: model.display_name,
      description: model.description,
      is_active: model.is_active,
      is_recommended: model.is_recommended,
      cost_tier: model.cost_tier,
    });
    setShowAddModel(true);
  };

  const handleUpdateModel = async () => {
    if (!editingModel) return;

    try {
      const result = await aiModelsApi.updateModel(editingModel.id, {
        model_id: modelForm.model_id,
        display_name: modelForm.display_name,
        description: modelForm.description,
        is_active: modelForm.is_active,
        is_recommended: modelForm.is_recommended,
        cost_tier: modelForm.cost_tier,
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update model');
      }

      setModels(models.map(m =>
        m.id === editingModel.id ? result.data : m
      ));

      setEditingModel(null);
      setShowAddModel(false);
      setModelForm({
        provider_id: 'anthropic',
        model_id: '',
        display_name: '',
        description: '',
        is_active: true,
        is_recommended: false,
        cost_tier: 'medium',
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update model');
    }
  };

  const handleDeleteModel = async (id: string) => {
    if (!confirm('Are you sure you want to delete this model?')) {
      return;
    }

    try {
      const result = await aiModelsApi.deleteModel(id);

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete model');
      }

      setModels(models.filter(m => m.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete model');
    }
  };

  const getCostTierColor = (tier: string) => {
    switch (tier) {
      case 'low': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-blue-100 text-blue-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'premium': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCostTierLabel = (tier: string) => {
    switch (tier) {
      case 'low': return '💰 Low Cost';
      case 'medium': return '💰💰 Medium Cost';
      case 'high': return '💰💰💰 High Cost';
      case 'premium': return '💎 Premium';
      default: return tier;
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading AI models and providers..." />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="h-7 w-7 text-purple-600" />
            AI Models & Providers
          </h1>
          <p className="text-gray-600 mt-1">Configure available AI providers and models for organizations</p>
        </div>
      </div>

      {/* Providers Section */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">AI Providers</h2>
          <Button
            onClick={() => {
              setEditingProvider(null);
              setProviderForm({ name: '', display_name: '', is_active: true });
              setShowAddProvider(true);
            }}
            icon={Plus}
            variant="primary"
            size="sm"
          >
            Add Provider
          </Button>
        </div>

        {/* Add/Edit Provider Form */}
        {showAddProvider && (
          <div className="mb-4 p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
            <h3 className="text-lg font-semibold mb-3">
              {editingProvider ? 'Edit Provider' : 'Add New Provider'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provider Name (ID) *
                </label>
                <input
                  type="text"
                  value={providerForm.name}
                  onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="anthropic"
                  disabled={!!editingProvider}
                />
                <p className="text-xs text-gray-500 mt-1">Lowercase, no spaces (e.g., anthropic, openai)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={providerForm.display_name}
                  onChange={(e) => setProviderForm({ ...providerForm, display_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Anthropic (Claude)"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={providerForm.is_active}
                    onChange={(e) => setProviderForm({ ...providerForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                onClick={editingProvider ? handleUpdateProvider : handleAddProvider}
                icon={Save}
                variant="primary"
                size="sm"
              >
                {editingProvider ? 'Update' : 'Add'} Provider
              </Button>
              <Button
                onClick={() => {
                  setShowAddProvider(false);
                  setEditingProvider(null);
                  setProviderForm({ name: '', display_name: '', is_active: true });
                }}
                icon={X}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Providers List */}
        <div className="space-y-2">
          {providers.map((provider) => (
            <div key={provider.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Brain className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="font-semibold">{provider.display_name}</div>
                  <div className="text-sm text-gray-500">ID: {provider.name}</div>
                </div>
                {provider.is_active ? (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                ) : (
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">Inactive</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleEditProvider(provider)}
                  icon={Edit2}
                  variant="ghost"
                  size="sm"
                >
                  <span className="sr-only">Edit</span>
                </Button>
                <Button
                  onClick={() => handleDeleteProvider(provider.id)}
                  icon={Trash2}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <span className="sr-only">Delete</span>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Models Section */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">AI Models</h2>
          <Button
            onClick={() => {
              setEditingModel(null);
              setModelForm({
                provider_id: providers[0]?.id || 'anthropic',
                model_id: '',
                display_name: '',
                description: '',
                is_active: true,
                is_recommended: false,
                cost_tier: 'medium',
              });
              setShowAddModel(true);
            }}
            icon={Plus}
            variant="primary"
            size="sm"
          >
            Add Model
          </Button>
        </div>

        {/* Add/Edit Model Form */}
        {showAddModel && (
          <div className="mb-4 p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
            <h3 className="text-lg font-semibold mb-3">
              {editingModel ? 'Edit Model' : 'Add New Model'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provider *
                </label>
                <select
                  value={modelForm.provider_id}
                  onChange={(e) => setModelForm({ ...modelForm, provider_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {providers.filter(p => p.is_active).map(provider => (
                    <option key={provider.id} value={provider.id}>
                      {provider.display_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model ID *
                </label>
                <input
                  type="text"
                  value={modelForm.model_id}
                  onChange={(e) => setModelForm({ ...modelForm, model_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="claude-3-5-sonnet-20241022"
                />
                <p className="text-xs text-gray-500 mt-1">Exact model identifier from provider</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={modelForm.display_name}
                  onChange={(e) => setModelForm({ ...modelForm, display_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Claude 3.5 Sonnet (Latest)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost Tier *
                </label>
                <select
                  value={modelForm.cost_tier}
                  onChange={(e) => setModelForm({ ...modelForm, cost_tier: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="low">Low Cost</option>
                  <option value="medium">Medium Cost</option>
                  <option value="high">High Cost</option>
                  <option value="premium">Premium</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={modelForm.description}
                  onChange={(e) => setModelForm({ ...modelForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={2}
                  placeholder="Brief description of model capabilities..."
                />
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={modelForm.is_active}
                    onChange={(e) => setModelForm({ ...modelForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={modelForm.is_recommended}
                    onChange={(e) => setModelForm({ ...modelForm, is_recommended: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Recommended</span>
                </label>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                onClick={editingModel ? handleUpdateModel : handleAddModel}
                icon={Save}
                variant="primary"
                size="sm"
              >
                {editingModel ? 'Update' : 'Add'} Model
              </Button>
              <Button
                onClick={() => {
                  setShowAddModel(false);
                  setEditingModel(null);
                }}
                icon={X}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Models List - Grouped by Provider */}
        <div className="space-y-4">
          {providers.map(provider => {
            const providerModels = models.filter(m => m.provider_id === provider.id);

            if (providerModels.length === 0) return null;

            return (
              <div key={provider.id}>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{provider.display_name}</h3>
                <div className="space-y-2">
                  {providerModels.map(model => (
                    <div key={model.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border-l-4 border-purple-400">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="font-semibold">{model.display_name}</div>
                          {model.is_recommended && (
                            <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">⭐ Recommended</span>
                          )}
                          {model.is_active ? (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">Inactive</span>
                          )}
                          <span className={`px-2 py-0.5 text-xs rounded ${getCostTierColor(model.cost_tier)}`}>
                            {getCostTierLabel(model.cost_tier)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mb-1">{model.description}</div>
                        <div className="text-xs text-gray-500 font-mono">Model ID: {model.model_id}</div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          onClick={() => handleEditModel(model)}
                          icon={Edit2}
                          variant="ghost"
                          size="sm"
                        >
                          <span className="sr-only">Edit</span>
                        </Button>
                        <Button
                          onClick={() => handleDeleteModel(model.id)}
                          icon={Trash2}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <span className="sr-only">Delete</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Info Card */}
      <Card>
        <h3 className="text-lg font-semibold mb-2">About AI Models Configuration</h3>
        <div className="text-sm text-gray-600 space-y-2">
          <p>
            <strong>Providers:</strong> Configure which AI providers are available for organizations to use.
            Each provider represents a different AI service (e.g., Anthropic, OpenAI).
          </p>
          <p>
            <strong>Models:</strong> Add specific model versions for each provider. Organizations can select
            which model to use for their AI analysis tasks.
          </p>
          <p>
            <strong>Cost Tiers:</strong> Help organizations understand the relative cost of different models:
          </p>
          <ul className="list-disc list-inside ml-4">
            <li>💰 Low Cost: Most economical, good for high-volume processing</li>
            <li>💰💰 Medium Cost: Balanced cost and performance</li>
            <li>💰💰💰 High Cost: Advanced capabilities at higher cost</li>
            <li>💎 Premium: Top-tier performance, premium pricing</li>
          </ul>
          <p>
            <strong>Recommended:</strong> Mark your preferred default model for new organizations.
          </p>
        </div>
      </Card>
    </div>
  );
};
